import type { DateRangeType } from '@medly-components/core';
import { DEFAULT_DATE_FORMAT } from 'constants/common';
import { add, format, formatRFC3339 } from 'date-fns';

// Get an array of dates between start and end date
export const getDatesArray = (startDate: Date, endDate: Date) => {
    const dates = [];
    for (let date = startDate; date <= endDate; date = add(date, { days: 1 })) {
        date.setHours(0, 0, 0, 0);
        dates.push(date);
    }
    return dates;
};

export const isExpiredDate = (date: string) => {
    return new Date(date).getTime() < new Date().getTime();
};

export const createDate = (date: string | Date, time?: string) => {
    const result: Date = new Date(date);
    // @ts-expect-error
    time && result.setHours(...time.split(':').map(Number));
    return result;
};

export const isTimePresent = (date: string | Date) => format(createDate(date), 'HH:mm') !== '00:00';

export const getTimeFromDate = (date?: string | Date | null) => {
    if (date) {
        const time = format(createDate(date), 'HH:mm');
        return time !== '00:00' ? time : '';
    } else {
        return '';
    }
};

export const getFormattedDate = (date?: string | Date | null, displayFormat = DEFAULT_DATE_FORMAT) =>
    date ? format(createDate(date), displayFormat) : '';

export const getRFC3339Date = (date?: string | Date | null, time?: string) => {
    if (!date) return '';
    return formatRFC3339(createDate(date, time));
};

export const isDateInBetweenDateRange = (date?: Date | null, dates?: DateRangeType) => {
    const { startDate, endDate } = dates || {};
    if (!date) {
        return false;
    }
    return (
        (startDate ? date >= createDate(startDate, '00:00:00:00') : true) && (endDate ? date <= createDate(endDate, '00:00:00:00') : true)
    );
};

export const filterOnDates = <Data extends { startDate?: Date | string | null | undefined; endDate?: Date | string | null | undefined }>(
    data: Data,
    dates: DateRangeType
) => {
    const { startDate: filterStartDate, endDate: filterEndDate } = dates;
    const startDate = data.startDate ? createDate(data.startDate, '00:00:00:00') : null;
    const endDate = data.endDate ? createDate(data.endDate, '00:00:00:00') : null;

    if (!(filterStartDate || filterEndDate)) {
        return true;
    }

    if (!startDate && !endDate) {
        return false;
    }

    return (
        // Check if the start date is between the filter start and end date
        isDateInBetweenDateRange(startDate, dates) ||
        // Check if the end date is between the filter start and end date
        isDateInBetweenDateRange(endDate, dates) ||
        // Check if the filter start date is between the start and end date
        isDateInBetweenDateRange(filterStartDate, { startDate, endDate }) ||
        // Check if the filter end date is between the start and end date
        isDateInBetweenDateRange(filterStartDate, { startDate, endDate })
    );
};

export const getToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};

export function formatDateTime(dateStr?: string | null | undefined): string {
    if (!dateStr) {
        return '';
    }
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'long'
    };
    const formattedTime = date.toLocaleTimeString('en-US', options);

    const timezoneAbbreviation = formattedTime
        .split(' ')
        ?.slice(2)
        ?.map(word => word.charAt(0))
        ?.join('');

    const result = `${formattedTime.split(' ')?.[0]} ${formattedTime.split(' ')?.[1]} ${timezoneAbbreviation}`;
    return result;
}
